import React, { createContext, useContext, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { ChildrenPropType } from '../../../../prop-types/children';
import { sortByPropAsc } from '../../../../services/sort-service';
import Global from '../../../../store/global-selectors';
import { useEnhancedReturnsContext } from '../../context/enhanced-returns-context-provider';
import { useSalesOrderIdContext } from '../../../context/sales-order-id-context-provider';
import { useCreateReturnContext } from '../../context/create-return-provider';
import { usePendingReturnContext } from '../../context/pending-return-context-provider';
import { NewReturnFulfillmentId } from '../../hooks/use-pending-return';
import { useCanCreateEnhancedReturn } from '../../../../store/returns/returns.helpers';

const initialValue = [];

const ReturnsPanelContext = createContext(initialValue);

export const useReturnsPanelContext = () => useContext(ReturnsPanelContext);

export const ReturnsPanelContextProvider = ({ children }) => {
  const salesOrderId = useSalesOrderIdContext();
  const { hasPendingReturn } = usePendingReturnContext();
  const { isLoadingCreatedReturn } = useCreateReturnContext();
  const { returns: enhancedReturns, pendingReturn } = useEnhancedReturnsContext();
  const shouldUseEnhancedReturn = useCanCreateEnhancedReturn(salesOrderId);

  const showPendingReturnTab = hasPendingReturn || isLoadingCreatedReturn;

  const salesOrder = useSelector((state) => Global.selectEntitiesSalesOrderById(state, { salesOrderId })) || emptyObject;
  const returnIds = salesOrder.returnIds || [];
  const unsortedReturns = useSelector((state) => Global.selectEntitiesReturnsByIds(state, { fulfillmentIds: returnIds })) || emptyArray;

  let returnCounter = 1;

  const returns = unsortedReturns.sort(sortByPropAsc('createDateTime')).map((returnShipment) => {
    const { fulfillmentId } = returnShipment;
    return {
      returnFulfillmentId: fulfillmentId,
      counter: returnCounter++,
      isNewReturn: false,
    };
  });

  const value = useMemo(() => {
    const enhancedReturnsList = [...enhancedReturns];
    const standardReturnsList = [...returns];

    if (showPendingReturnTab) {
      if (shouldUseEnhancedReturn && pendingReturn) {
        enhancedReturnsList.push(pendingReturn);
      } else if (!shouldUseEnhancedReturn) {
        standardReturnsList.push({
          returnFulfillmentId: NewReturnFulfillmentId,
          counter: returnCounter++,
          isNewReturn: true,
        });
      }
    }

    return {
      enhancedReturns: enhancedReturnsList,
      standardReturns: standardReturnsList,
    };
  }, [enhancedReturns, returns, returnCounter, showPendingReturnTab, pendingReturn, shouldUseEnhancedReturn]);

  return <ReturnsPanelContext.Provider value={value}>{children}</ReturnsPanelContext.Provider>;
};

ReturnsPanelContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
};
